<template>
  <div class="legal-page">
    <!-- Intro Section -->
    <PolicyIntroComp :content="introContent" />

    <!-- Refund Content Section -->
    <PolicyContentComp :content="contentData" />
  </div>
</template>

<script>
import { onMounted, ref } from 'vue'
import WOW from 'wowjs'
import PolicyIntroComp from '@/components/PolicyIntroComp.vue'
import PolicyContentComp from '@/components/PolicyContentComp.vue'

export default {
  name: 'RefundPolicyPage',
  components: {
    PolicyIntroComp,
    PolicyContentComp
  },
  setup() {
    onMounted(() => {
      // Initialize WOW.js
      new WOW.WOW({
        mobile: false
      }).init()
    })

    // Intro content object
    const introContent = ref({
      sectionLabel: "Refund Policy",
      mainTitle: "Refund Policy",
      description: "We understand that circumstances may change. This policy outlines our refund procedures for different types of services and project stages."
    })

    // Content object for Refund Policy
    const contentData = ref({
      sections: [
        {
          title: "1. Refund Eligibility",
          paragraphs: [
            {
              type: "text",
              content: "Refunds are available under the following circumstances:"
            },
            {
              type: "unordered",
              items: [
                "Project cancellation before development begins (100% refund)",
                "Project cancellation during discovery phase (90% refund)",
                "Project cancellation after first milestone (prorated refund)",
                "Service not delivered as agreed in contract",
                "Technical issues preventing service delivery"
              ]
            }
          ]
        },
        {
          title: "2. Before Development Begins",
          paragraphs: [
            {
              type: "text",
              content: "If you cancel your project before any development work begins, you are eligible for a full refund of any payments made. This includes:"
            },
            {
              type: "unordered",
              items: [
                "Initial consultation fees (if applicable)",
                "Project setup fees",
                "Any advance payments made"
              ]
            },
            {
              type: "text",
              content: "<strong>Processing Time:</strong> 5-7 business days"
            }
          ]
        },
        {
          title: "3. During Discovery Phase",
          paragraphs: [
            {
              type: "text",
              content: "If you cancel during the discovery and planning phase (typically the first 1-2 weeks), you are eligible for a 90% refund. The 10% retention covers:"
            },
            {
              type: "unordered",
              items: [
                "Initial consultation and planning time",
                "Project setup and documentation",
                "Administrative processing costs"
              ]
            },
            {
              type: "text",
              content: "<strong>Processing Time:</strong> 7-10 business days"
            }
          ]
        },
        {
          title: "4. Milestone-Based Refunds",
          paragraphs: [
            {
              type: "text",
              content: "For projects cancelled after development has begun, refunds are calculated based on completed milestones:"
            },
            {
              type: "unordered",
              items: [
                "<strong>25% Complete:</strong> 75% refund",
                "<strong>50% Complete:</strong> 50% refund",
                "<strong>75% Complete:</strong> 25% refund",
                "<strong>90%+ Complete:</strong> No refund (delivery of completed work)"
              ]
            },
            {
              type: "text",
              content: "<strong>Processing Time:</strong> 10-14 business days"
            }
          ]
        },
        {
          title: "5. Non-Refundable Items",
          paragraphs: [
            {
              type: "text",
              content: "The following items are not eligible for refunds:"
            },
            {
              type: "unordered",
              items: [
                "Third-party software licenses and subscriptions",
                "Domain registration and hosting fees (if already purchased)",
                "Custom design work that has been approved and implemented",
                "Content creation and copywriting services",
                "Training and consultation sessions already completed"
              ]
            }
          ]
        },
        {
          title: "6. Refund Request Process",
          paragraphs: [
            {
              type: "text",
              content: "To request a refund, please follow these steps:"
            },
            {
              type: "ordered",
              items: [
                "Contact us at refunds@sogdo.com with your project details",
                "Provide reason for cancellation and any supporting documentation",
                "We will review your request within 2 business days",
                "Upon approval, refund will be processed to original payment method",
                "You will receive confirmation once refund is completed"
              ]
            }
          ]
        },
        {
          title: "7. Refund Processing",
          paragraphs: [
            {
              type: "text",
              content: "Refunds are processed to the original payment method used for the transaction. Processing times vary by payment method:"
            },
            {
              type: "unordered",
              items: [
                "<strong>Credit/Debit Cards:</strong> 5-10 business days",
                "<strong>Bank Transfers:</strong> 7-14 business days",
                "<strong>PayPal:</strong> 3-5 business days",
                "<strong>Wire Transfers:</strong> 10-15 business days"
              ]
            }
          ]
        },
        {
          title: "8. Dispute Resolution",
          paragraphs: [
            {
              type: "text",
              content: "If you disagree with our refund decision, you may:"
            },
            {
              type: "unordered",
              items: [
                "Request a review by our management team",
                "Provide additional documentation or evidence",
                "Request mediation through a third-party service",
                "Escalate to legal resolution if necessary"
              ]
            }
          ]
        },
        {
          title: "9. Special Circumstances",
          paragraphs: [
            {
              type: "text",
              content: "We may consider refunds in special circumstances such as:"
            },
            {
              type: "unordered",
              items: [
                "Medical emergencies affecting project timeline",
                "Natural disasters or force majeure events",
                "Significant changes in business requirements",
                "Technical limitations not disclosed during planning"
              ]
            },
            {
              type: "text",
              content: "Each case is evaluated individually and may require additional documentation."
            }
          ]
        },
        {
          title: "10. Contact Information",
          paragraphs: [
            {
              type: "text",
              content: "For refund requests and questions, please contact us at:"
            }
          ],
          contactInfo: [
            {
              label: "Refund Requests",
              value: "refunds@sogdo.com"
            },
            {
              label: "General Inquiries",
              value: "hello@sogdo.com"
            },
            {
              label: "Phone",
              value: "+1 (555) 123-4567"
            },
            {
              label: "Address",
              value: "123 Tech Street, Innovation District, San Francisco, CA 94105"
            }
          ]
        }
      ]
    })

    return {
      introContent,
      contentData
    }
  }
}
</script>